import * as _ from 'lodash';
import { DataShape } from 'redux-form';

import * as productsAPI from 'login-apis/productsAPI';

interface BaseParams {
  loanPurpose?: string;
  refiPurpose?: string;
  countyName: string;
  estimatedCreditScore: string;
  stateCode: string;
  propertyType: string;
  propertyUsageType: string;
  veteranStatus?: string;
  previousVA?: boolean;
  receivingVADisabilityIncome?: boolean;
}

interface QueryParams extends BaseParams {
  downPayment: number;
  propertyValue: number;
}

export interface UpfrontRatesParams extends BaseParams {
  loanAmount?: string;
  propertyValue?: string;
  cashDownPayment?: string;
  purchasePrice?: string;
  loanType: string;
}

export interface UpfrontRate {
  apr: number;
  displayName: string;
  interestRate: number;
  monthlyPayment: number;
}

interface UpfrontRatesResult {
  upfrontRates: UpfrontRate[];
  disclaimer: string | null;
}

const UPFRONT_RATE_FIELDS = ['apr', 'displayName', 'interestRate', 'monthlyPayment'];

export async function getUpfrontRates(values: DataShape): Promise<UpfrontRatesResult> {
  const params: QueryParams = {
    loanPurpose: values.loanPurpose,
    refiPurpose: values.refiPurpose,
    downPayment: values.loanPurpose === 'REFINANCE' ? values.propertyValue - values.loanAmount : values.cashDownPayment,
    countyName: _.get(values, 'propertyCounty.countyName'),
    estimatedCreditScore: values.estimatedCreditScore,
    propertyValue: values.loanPurpose === 'REFINANCE' ? values.propertyValue : values.purchasePrice,
    stateCode: _.get(values, 'propertyCounty.stateCode'),
    propertyType: values.propertyType,
    propertyUsageType: values.propertyUsageType,
    veteranStatus: values.veteranStatus,
    previousVA: values.previousVA,
    receivingVADisabilityIncome: values.receivingVADisabilityIncome,
  };
  const { data } = await productsAPI.getUpfrontRates(params);
  // Grab the first aprDisclaimer found. It's the same for all businessMessages because of what the pricing engine
  // returns.
  const aprDisclaimer = _.find(_.flatMap(data, rate => rate.businessMessages), msg => msg.type === 'AprDisclaimer');
  const disclaimer = aprDisclaimer ? aprDisclaimer.description : null;
  const upfrontRates: UpfrontRate[] = _.map(data, rate => _.pick(rate, UPFRONT_RATE_FIELDS) as UpfrontRate);

  return {
    upfrontRates,
    disclaimer,
  };
}

export function getUpfrontRatesParams(query: any): UpfrontRatesParams {
  return _.pick(query, [
    'loanPurpose',
    'refiPurpose',
    'propertyType',
    'propertyUsageType',
    'loanAmount',
    'propertyValue',
    'cashDownPayment',
    'purchasePrice',
    'stateCode',
    'countyName',
    'estimatedCreditScore',
    'veteranStatus',
    'previousVA',
    'receivingVADisabilityIncome',
    'loanType',
  ]) as UpfrontRatesParams;
}

export function formatUpfrontRatesParams(params: UpfrontRatesParams): DataShape {
  return {
    ...params,
    propertyCounty: {
      stateCode: params.stateCode,
      countyName: params.countyName,
      display: `${params.stateCode} - ${params.countyName}`,
    },
  };
}
